import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";

import { AllCommunityModule, type StateUpdatedEvent, type CellDoubleClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useSettingsStore } from "@user/store/settings_store";

import CellInfoPopup from "@user/views/stock/popup_cell_info";

import "./cell_monitoring.scoped.scss";

interface State {
  isContinueSearch: boolean,
  location12: string,
  searchType: string,
  isShowCellInfo: boolean
}

const defState: State = {
  isContinueSearch: true,
  location12: "",
  searchType: "",
  isShowCellInfo: false
};

interface SearchParams {
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const defColDef: CustomColDef = {
  resizable: true,
  sortable: true,
  cellStyle: (p) => ({
    textAlign: p.colDef.field == "typeName" ? "center" : "right",
    cursor: p.node.rowPinned ? "default" : "pointer"
  })
};

function CellInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "구분", field: "typeName", width: 150, summaryLabel: "합계" },
    { headerName: "총CELL수", headerClass: "hd-ta-right", field: "totalCellQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "사용가능CELL", headerClass: "hd-ta-right", field: "totalCellUseQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "입고작업", headerClass: "hd-ta-right", field: "inWorkQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "출고작업", headerClass: "hd-ta-right", field: "outWorkQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "실사작업", headerClass: "hd-ta-right", field: "auditWorkQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "공CELL", headerClass: "hd-ta-right", field: "emptyCellQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "금지CELL", headerClass: "hd-ta-right", field: "disableCellQty", width: 150, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "오류CELL", headerClass: "hd-ta-right", field: "errorCellQty", width: 150, valueFormatter: valueFormatter.number, summable: true }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
  }), []);
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellDoubleClicked: (e: CellDoubleClickedEvent) => {
      if (!e.node.rowPinned) {
        const field = e.colDef.field;
        let searchType = "";
        if (field === "totalCellUseQty") searchType = "USE";
        else if (field === "inWorkQty") searchType = "IN";
        else if (field === "outWorkQty") searchType = "OUT";
        else if (field === "auditWorkQty") searchType = "AUDIT";
        else if (field === "emptyCellQty") searchType = "EMPTY";
        else if (field === "disableCellQty") searchType = "DISABLE";
        else if (field === "errorCellQty") searchType = "ERROR";
        openPopupCellInfo(e.data.typeId, searchType);
      }
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    if (state.isContinueSearch) {
      const intervalId = setInterval(() => {
        searchInfos();
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [state.isContinueSearch, isActiveTab]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/cell_info.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: (data.infos ?? []).filter((row: any) => row.totalYn === "N"),
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/cell_info", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/cell_info", searchParams);
  }, [result, router, updateSearchParams]);

  async function doSearchContinue(search: boolean) {
    setState(prev => ({ ...prev, isContinueSearch: search }));
    if (search) {
      await searchInfos();
    }
  }

  async function openPopupCellInfo(location12: string, searchType: string) {
    setState(prev => ({ ...prev, isContinueSearch: false, isShowCellInfo: true, location12: location12, searchType: searchType }));
  }

  async function closePopupCellInfo(_data: any) {
    setState(prev => ({ ...prev, isShowCellInfo: false }));
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>CELL 정보</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            {state.isContinueSearch == true && <Button variant="outlined" onClick={() => doSearchContinue(false)}>연속조회중지</Button>}
            {state.isContinueSearch == false && <Button variant="outlined" onClick={() => doSearchContinue(true)}>연속조회시작</Button>}
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* CELL 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={defColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellDoubleClicked={gridCallbacks.onCellDoubleClicked}
            />
          </div>
        </AgGridProvider>
        {/*// CELL 정보 */}
        {state.isShowCellInfo && <CellInfoPopup isShow={state.isShowCellInfo} onClose={closePopupCellInfo} id={props.id} data={{location12: state.location12, searchType: state.searchType}} onRefresh={searchInfos} />}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(CellInfo);
